import { useState, useRef } from 'react';
import { useActiveSection } from '../hooks/useActiveSection';

// Geometry lifted from the original DownloadButton.png (760x268 source artwork).
// The visible pill is 616x192 of that canvas; each value is a ratio of the source
// height so both call sites reproduce the artwork exactly at their own size.
const DL = {
    w: 616 / 268,
    h: 192 / 268,
    radius: 46 / 268,
    gap: 50 / 268,
    font: 61 / 268,
    iconW: 68 / 268,
    iconH: 71 / 268,
};

const DownloadButton = ({
    height,
    onClick,
    className = '',
}: {
    height: number;
    onClick: () => void;
    className?: string;
}) => (
    <button
        onClick={onClick}
        className={`items-center justify-center whitespace-nowrap bg-[#6B41BC] text-white shrink-0 hover:scale-105 transition-all active:scale-95 cursor-pointer ${className}`}
        style={{
            width: DL.w * height,
            height: DL.h * height,
            borderRadius: DL.radius * height,
            gap: DL.gap * height,
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize: DL.font * height,
            lineHeight: 1,
            boxShadow: `0 ${0.077 * height}px ${0.2 * height}px rgba(0,0,0,0.22)`,
        }}
    >
        Download
        <svg
            viewBox="0 0 68 71"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth={7.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: DL.iconW * height, height: DL.iconH * height }}
        >
            <path d="M33.5 3.75V55.5" />
            <path d="M8 30l25.5 25.5L59 30" />
            <path d="M4.5 67h58" />
        </svg>
    </button>
);

// Listed in the order the sections appear on the page, which the scrollspy relies on.
const navLinks = [
    { label: 'Home', id: 'hero' },
    { label: 'Features', id: 'how-it-works' },
    { label: 'About Us', id: 'about-us' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Testimonials', id: 'testimonials' },
];

const navIds = navLinks.map((link) => link.id);

const Navbar = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navRef = useRef<HTMLElement>(null);
    const activeId = useActiveSection(navIds, navRef);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
        setIsMobileMenuOpen(false);
    };

    return (
        <>
            {/* Navbar Container */}
            <div className="fixed top-0 left-0 right-0 z-50 flex justify-center mt-[20px] md:mt-[30px] lg:mt-[15px] px-4 md:px-6 lg:px-20 pointer-events-none">
                <nav ref={navRef} className="w-full max-w-[1440px] bg-[#8E57FA66]/40 backdrop-blur-2xl border-[1px] border-[#FFFFFF4D]/30 rounded-[16px] md:rounded-[20px] px-[16px] md:px-[20px] lg:px-[28px] py-[10px] md:py-[12px] lg:py-[14px] flex items-center justify-between shadow-2xl pointer-events-auto">
                    {/* Logo Section */}
                    <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
                        <div className="bg-white rounded-xl md:rounded-2xl border border-white/10 shrink-0">
                            <img
                                src="/heroSection/NavbarLogo-256.png"
                                alt="Eddy AI Logo"
                                width={44}
                                height={44}
                                fetchPriority="high"
                                decoding="async"
                                className="h-[32px] md:h-[38px] lg:h-[44px] w-auto"
                            />
                        </div>
                        <span
                            style={{
                                fontWeight: 700,
                                fontSize: "clamp(18px, 4vw, 24px)",
                                lineHeight: "1.5",
                                letterSpacing: "0%",
                                verticalAlign: "middle",
                            }}
                            className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent"
                        >
                            Eddy AI
                        </span>
                    </div>

                    {/* Desktop Navigation Links */}
                    <ul
                        className="hidden lg:flex items-center gap-8 xl:gap-12"
                        style={{
                            fontSize: "16px",
                            lineHeight: "24px",
                            letterSpacing: "1%",
                            verticalAlign: "middle",
                            fontFamily: "'Poppins', sans-serif",
                        }}
                    >
                        {navLinks.map((link) => (
                            <li key={link.id}>
                                <button
                                    type="button"
                                    onClick={() => scrollToSection(link.id)}
                                    aria-current={activeId === link.id ? 'true' : undefined}
                                    className={`cursor-pointer active:scale-95 transition-all ${
                                        activeId === link.id
                                            ? 'text-white font-semibold'
                                            : 'text-[#DCCBFD] hover:text-white font-normal'
                                    }`}
                                    style={{ fontWeight: activeId === link.id ? 600 : 400 }}
                                >
                                    {link.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* Download Button - Desktop */}
                    <DownloadButton
                        height={52}
                        onClick={() => scrollToSection('cta')}
                        className="hidden lg:inline-flex"
                    />

                    {/* Mobile Menu Button */}
                    <button
                        onClick={toggleMobileMenu}
                        className="lg:hidden flex flex-col gap-1.5 p-2 cursor-pointer hover:scale-105 active:scale-95 transition-all"
                        aria-label="Toggle menu"
                    >
                        <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                        <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
                        <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                    </button>
                </nav>
            </div>

            {/* Mobile Menu Overlay */}
            {/* Decorative scrim: the drawer's own Close button is the accessible control,
                so this stays out of the tab order. */}
            <button
                type="button"
                tabIndex={-1}
                aria-hidden="true"
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={toggleMobileMenu}
            />

            {/* Mobile Menu Drawer */}
            <div
                className={`fixed top-0 right-0 h-full w-[280px] sm:w-[320px] bg-[#1a1425] border-l border-[#FFFFFF4D]/30 z-50 lg:hidden transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
            >
                <div className="flex flex-col h-full p-6">
                    {/* Close Button */}
                    <button
                        onClick={toggleMobileMenu}
                        className="self-end mb-8 p-2 hover:bg-white/10 rounded-lg transition-colors"
                        aria-label="Close menu"
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Mobile Navigation Links */}
                    <ul
                        className="flex flex-col gap-6 mb-8"
                        style={{
                            fontSize: "18px",
                            lineHeight: "28px",
                            fontFamily: "'Poppins', sans-serif",
                        }}
                    >
                        {navLinks.map((link) => (
                            <li key={link.id}>
                                {/* w-full/text-left keep the button flush with the old <li> box */}
                                <button
                                    type="button"
                                    onClick={() => scrollToSection(link.id)}
                                    aria-current={activeId === link.id ? 'true' : undefined}
                                    className={`w-full text-left cursor-pointer active:scale-95 transition-all ${
                                        activeId === link.id
                                            ? 'text-white font-semibold border-b border-white/10 pb-4'
                                            : 'text-[#DCCBFD] hover:text-white font-normal'
                                    }`}
                                    style={{ fontWeight: activeId === link.id ? 600 : 400 }}
                                >
                                    {link.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {/* Download Button - Mobile */}
                    <DownloadButton
                        height={48}
                        onClick={() => scrollToSection('cta')}
                        className="flex mt-auto"
                    />
                </div>
            </div>
        </>
    );
};

export default Navbar;
